"use client";

import { useState, useRef, useEffect, useCallback, useTransition } from "react";
import { castLCKVoteAction, undoLCKVoteAction, getLCKVoters } from "../actions/lck";
import { useRouter } from "next/navigation";
import { clog } from "@/lib/client-log";

interface Voter {
  userId: string;
  user: { displayName: string | null; role: string };
}

interface NameData {
  id: string;
  name: string;
  origin: string | null;
  meaning: string | null;
  winCount: number;
  lossCount: number;
  tieCount: number;
  lcKVoteCount: number;
  resurrected: boolean;
  lcKEligible?: boolean;
  lckOpen?: boolean;
}

const HOLD_MS = 6000;

interface Props {
  name: NameData;
  lckRequired: number;
  alreadyVoted: boolean;
  onClose: () => void;
}

export default function EliminatedNameModal({ name: initial, lckRequired: initialLckRequired, alreadyVoted, onClose }: Props) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const [voted,        setVoted]        = useState(alreadyVoted);
  const [resurrected,  setResurrected]  = useState(initial.resurrected);
  const [voteCount,    setVoteCount]    = useState(initial.lcKVoteCount);
  const [lckRequired,  setLckRequired]  = useState(initialLckRequired);
  const [voters,       setVoters]       = useState<Voter[] | null>(null);
  const [voterLoading, setVoterLoading] = useState(false);

  // Fresh name data (meaning, origin) fetched on open
  const [origin,      setOrigin]      = useState(initial.origin);
  const [meaning,     setMeaning]     = useState(initial.meaning);
  const [dataLoading, setDataLoading] = useState(!initial.meaning && !initial.origin);

  // hold-to-vote
  const [fillPct,   setFillPct]   = useState(0);
  const [holding,   setHolding]   = useState(false);
  const [justVoted, setJustVoted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const holdStart = useRef<number | null>(null);
  const rafRef    = useRef<number | null>(null);
  const didFire   = useRef(false);
  // Set synchronously the instant a hold completes, before startTransition's
  // isPending flips — closes the gap where cancelHold() (fired by the same
  // pointerup that ends a successful hold) reset didFire and let a second
  // hold start while the first vote was still in flight on a slow request.
  const submittedRef = useRef(false);

  const canVote = !voted && !resurrected && initial.lcKEligible && initial.lckOpen && !isPending && !submittedRef.current;
  const canUndo = voted && !resurrected && !initial.resurrected && !isPending;

  // ── Fetch fresh name data + voters on open ───────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function loadAll() {
      // Fetch fresh name details on open — the modal is often opened from a
      // page/PWA tab that's been sitting idle for a while (server-rendered
      // props go stale the moment the page stops being freshly navigated to,
      // and this app's iOS-PWA sessions can sit open for a very long time).
      // Refreshes meaning/origin AND the vote count + resurrection threshold,
      // both of which used to come only from the initial server-render props.
      try {
        const res = await fetch(`/api/name?id=${initial.id}`);
        if (res.ok) {
          const fresh = await res.json();
          if (!cancelled) {
            setOrigin(fresh.origin ?? null);
            setMeaning(fresh.meaning ?? null);
            if (typeof fresh.lcKVoteCount === "number") setVoteCount(fresh.lcKVoteCount);
            if (typeof fresh.lckRequired === "number") setLckRequired(fresh.lckRequired);
            if (typeof fresh.resurrected === "boolean") setResurrected(fresh.resurrected);
          }
        }
      } catch (err) {
        clog.warn("elim-modal", "name fetch failed", { error: String(err) });
      } finally {
        if (!cancelled) setDataLoading(false);
      }

      // Fetch voters in parallel
      try {
        setVoterLoading(true);
        const data = await getLCKVoters(initial.id);
        if (!cancelled) setVoters(data);
      } catch (err) {
        clog.warn("elim-modal", "voters fetch failed", { error: String(err) });
      } finally {
        if (!cancelled) setVoterLoading(false);
      }
    }

    loadAll();
    return () => { cancelled = true; };
  }, [initial.id]);

  // ── Hold mechanics ───────────────────────────────────────────────────────────
  function cancelHold() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    holdStart.current = null;
    setHolding(false);
    setFillPct(0);
    didFire.current = false;
  }

  function startHold(e: React.PointerEvent) {
    if (!canVote || didFire.current || submittedRef.current) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    holdStart.current = Date.now();
    setHolding(true);
    didFire.current = false;
    function tick() {
      if (!holdStart.current) return;
      const pct = Math.min(100, ((Date.now() - holdStart.current) / HOLD_MS) * 100);
      setFillPct(pct);
      if (pct < 100) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        didFire.current = true;
        holdStart.current = null;
        setHolding(false);
        setFillPct(0);
        fireVote();
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  }

  function fireVote() {
    // Set before startTransition — React's isPending flips a tick later, and
    // the pointerup that ends this same successful hold calls cancelHold()
    // (resetting didFire) almost immediately, so without this ref a second
    // hold could start while the first vote is still in flight on a slow
    // request (the root cause of the "vote fails partway through" reports —
    // the vote had actually already succeeded, the UI just looked hung).
    submittedRef.current = true;
    setSubmitting(true);
    clog.info("elim-modal", "hold complete — casting LCK vote", { id: initial.id });
    startTransition(async () => {
      try {
        const result = await castLCKVoteAction(initial.id);
        if (result.success) {
          setVoted(true);
          setVoteCount((c) => c + 1);
          setJustVoted(true);
          if (result.resurrected) {
            setResurrected(true);
            setTimeout(() => router.refresh(), 1200);
          }
          const data = await getLCKVoters(initial.id);
          setVoters(data);
        } else {
          clog.warn("elim-modal", `vote rejected: ${result.error}`);
          submittedRef.current = false;
        }
      } catch (err) {
        clog.error("elim-modal", "vote threw", { error: String(err) });
        submittedRef.current = false;
      } finally {
        setSubmitting(false);
      }
    });
  }

  const handleUndo = useCallback(() => {
    startTransition(async () => {
      try {
        const result = await undoLCKVoteAction(initial.id);
        if (result.success) {
          setVoted(false);
          setVoteCount((c) => Math.max(0, c - 1));
          setJustVoted(false);
          const data = await getLCKVoters(initial.id);
          setVoters(data);
        }
      } catch (err) {
        clog.error("elim-modal", "undo threw", { error: String(err) });
      }
    });
  }, [initial.id]);

  const pct       = lckRequired > 0 ? Math.min(100, Math.round((voteCount / lckRequired) * 100)) : 0;
  const recordStr = initial.tieCount > 0
    ? `${initial.winCount}–${initial.tieCount}–${initial.lossCount}`
    : `${initial.winCount}–${initial.lossCount}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,5,32,0.75)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="bg-gradient-to-br from-[#1e0b4a] to-[#3b0764] px-6 pt-6 pb-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-3xl font-black text-white leading-tight line-through opacity-70">
                {initial.name}
              </h2>
              {origin ? (
                <p className="text-purple-300 text-sm mt-0.5">{origin}</p>
              ) : dataLoading ? (
                <div className="h-3 w-24 rounded bg-white/10 animate-pulse mt-1" />
              ) : null}
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              {resurrected ? (
                <span className="rounded-full px-2.5 py-0.5 text-xs font-black bg-orange-100 text-orange-700">🔥 Resurrected</span>
              ) : (
                <span className="rounded-full px-2.5 py-0.5 text-xs font-black bg-red-50 text-red-500">Eliminated</span>
              )}
              <button
                onClick={onClose}
                className="text-purple-400 hover:text-white text-xl leading-none mt-1 select-none"
                style={{ WebkitUserSelect: "none", userSelect: "none" }}
                aria-label="Close"
              >✕</button>
            </div>
          </div>

          {/* stat row */}
          <div className="flex gap-3 mt-4">
            {[
              { label: "Record",   value: recordStr },
              { label: "Votes",    value: `${voteCount}/${lckRequired}` },
              { label: "Progress", value: `${pct}%` },
            ].map(({ label, value }) => (
              <div key={label} className="flex-1 rounded-xl bg-white/10 px-3 py-2 text-center">
                <div className="text-white font-black text-lg leading-tight">{value}</div>
                <div className="text-purple-300 text-[10px] uppercase tracking-wider mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">

          {/* Meaning */}
          {meaning ? (
            <div>
              <h3 className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-1.5">Meaning</h3>
              <p className="text-gray-700 text-sm leading-relaxed">{meaning}</p>
            </div>
          ) : dataLoading ? (
            <div>
              <h3 className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-1.5">Meaning</h3>
              <div className="h-3 bg-purple-50 rounded animate-pulse w-3/4 mb-1.5" />
              <div className="h-3 bg-purple-50 rounded animate-pulse w-1/2" />
            </div>
          ) : null}

          {/* LCK progress */}
          {!resurrected && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[10px] font-black text-amber-500 uppercase tracking-widest">🍳 Last Chance Kitchen</h3>
                <span className="text-xs font-black text-amber-600">{voteCount} / {lckRequired} votes</span>
              </div>
              <div className="h-3 bg-amber-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-orange-400 rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-[10px] text-amber-400 mt-1.5">
                {lckRequired - voteCount > 0
                  ? `${lckRequired - voteCount} more vote${lckRequired - voteCount !== 1 ? "s" : ""} needed to resurrect`
                  : "Threshold reached!"}
              </p>
            </div>
          )}

          {resurrected && (
            <div className="rounded-2xl bg-orange-50 border border-orange-200 px-4 py-4 text-center">
              <div className="text-3xl mb-1">🔥</div>
              <div className="font-black text-orange-700 text-sm">Back in the tournament!</div>
              <div className="text-orange-500 text-xs mt-0.5">{initial.name} was resurrected by the family.</div>
            </div>
          )}

          {/* Who voted */}
          <div>
            <h3 className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-2">Who voted to rescue</h3>
            {voterLoading ? (
              <div className="flex gap-2">
                {[1, 2, 3].map((i) => <div key={i} className="h-8 w-20 rounded-xl bg-purple-50 animate-pulse" />)}
              </div>
            ) : voters && voters.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {voters.map((v) => (
                  <div key={v.userId} className="flex items-center gap-1.5 rounded-xl bg-purple-50 border border-purple-100 px-2.5 py-1.5">
                    <div className="w-5 h-5 rounded-full bg-purple-200 flex items-center justify-center text-[8px] font-black text-purple-700 shrink-0">
                      {(v.user.displayName ?? "?").slice(0, 2).toUpperCase()}
                    </div>
                    <span className="text-xs font-semibold text-purple-700 truncate max-w-[80px]">
                      {v.user.displayName ?? "Unknown"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No votes yet — be the first!</p>
            )}
          </div>

          {/* Hold-to-vote */}
          {initial.lcKEligible && initial.lckOpen && !resurrected && (
            <div className="pt-1">
              {!voted ? (
                <div
                  className="relative w-full rounded-2xl overflow-hidden select-none cursor-pointer"
                  style={{ WebkitUserSelect: "none", userSelect: "none", touchAction: "none" }}
                  onPointerDown={startHold}
                  onPointerUp={cancelHold}
                  onPointerLeave={cancelHold}
                  onPointerCancel={cancelHold}
                >
                  <div className="relative z-10 rounded-2xl border-2 border-amber-400 bg-amber-50 px-4 py-4 text-center overflow-hidden">
                    <div
                      className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-amber-400 to-amber-300 transition-none rounded-2xl"
                      style={{ height: `${fillPct}%` }}
                    />
                    <div className="relative z-10">
                      <div className="text-2xl mb-1">🍳</div>
                      <div className="font-black text-amber-900 text-sm">
                        {holding ? `Rescuing… ${Math.round(fillPct)}%` : submitting ? "Submitted — confirming…" : "Hold to rescue"}
                      </div>
                      <div className="text-amber-600 text-[10px] mt-0.5">
                        {holding ? "Keep holding…" : submitting ? "Your vote is in — just waiting on the server" : "Hold for 6 seconds to cast your LCK vote"}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-full rounded-2xl bg-amber-100 border border-amber-300 px-4 py-3 text-center">
                    <div className="text-xl mb-0.5">✅</div>
                    <div className="font-black text-amber-800 text-sm">{justVoted ? "Rescued!" : "You voted to rescue"}</div>
                    <div className="text-amber-600 text-[10px] mt-0.5">
                      {initial.name} needs {Math.max(0, lckRequired - voteCount)} more vote{Math.max(0, lckRequired - voteCount) !== 1 ? "s" : ""}
                    </div>
                  </div>
                  {canUndo && (
                    <button
                      onClick={handleUndo}
                      disabled={isPending}
                      className="text-xs text-gray-400 hover:text-gray-600 transition disabled:opacity-40 select-none"
                      style={{ WebkitUserSelect: "none", userSelect: "none" }}
                    >
                      Undo vote
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Not eligible */}
          {(!initial.lcKEligible || !initial.lckOpen) && !resurrected && (
            <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 text-center">
              <p className="text-gray-500 text-sm">
                {!initial.lckOpen ? "🔒 Last Chance Kitchen is closed." : "This name is not currently LCK eligible."}
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
